import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  TableSortConfig,
  type TableColumn,
} from "@/components/design-system/table";
import {
  deleteExistingStudent,
  fetchStudents,
  selectStudentActionLoading,
  selectStudentLoading,
  selectStudents,
  selectTotalItems,
} from "@/store/slices/studentSlice";
import { AppDispatch } from "@store";
import { Eye, FileText, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// Using the StudentModel from types
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import SchoolSelect from "@/components/design-system/schoolSelect";
import StudentTypeSelect from "@/components/design-system/StudentTypeSelect";
import StudentTypeBadge, {
  resolveStudentType,
} from "@/modules/User-Management/components/StudentTypeBadge";
import { getSchoolOptionsService } from "@/service/dashboard.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { SchoolOption } from "@/types/school.model";
import { StudentModel, StudentQuery, type StudentListType } from "@/types/student.model";
import {
  capitalizeFirstLetter,
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import { downloadStudentService } from "@/service/student.service";
import { Badge } from "@/components/design-system/badge";

// Extended interface for UI display purposes
interface StudentDisplay extends StudentModel {
  sno?: number;
  school?: {
    name: string;
  };
}

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function StudentListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );
  const [schoolOptions, setSchoolOptions] = useState<SchoolOption[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [selectedListType, setSelectedListType] = useState<StudentListType>("all");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const students = useSelector(selectStudents);
  const actionLoading = useSelector(selectStudentActionLoading);
  const loading = useSelector(selectStudentLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);

  const navigate = useNavigate();

  // Map column keys to API sort keys
  const sortKeyMap: Record<string, string> = {
    name: "NME",
    reg_no: "REG",
    email: "EML",
    grade: "GRDE",
    course: "CSE",
    age: "AGE",
    "school.name": "SCH",
  };

  // Handle sorting
  const handleSort = (newSortConfig: TableSortConfig | null) => {
    // Convert null to undefined for compatibility with the Table component
    setSortConfig(newSortConfig || undefined);
    // Reset to first page when sort changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const columns: TableColumn<StudentDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "reg_no",
      title: "Unique ID",
      dataIndex: "reg_no",
      minWidth: 145,
      maxWidth: 145,
      sortable: true,
      sortKey: "reg_no",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.reg_no}
        </div>
      ),
    },
    {
      key: "name",
      title: "Student Name",
      dataIndex: "name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      sortKey: "name",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "type",
      title: "Type",
      minWidth: 120,
      maxWidth: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center">
          <StudentTypeBadge type={resolveStudentType(record)} />
        </div>
      ),
    },
    {
      key: "school_name",
      title: "School Name",
      dataIndex: "school",
      minWidth: 278,
      maxWidth: 278,
      sortable: true,
      sortKey: "school.name",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.school?.name}
        </div>
      ),
    },

    {
      key: "grade",
      title: "Grade",
      minWidth: 148,
      maxWidth: 148,
      sortable: true,
      sortKey: "grade",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {Array.isArray(record.grade)
            ? record.grade
                .map((grade) =>
                  typeof grade === "object" && grade !== null
                    ? grade.grade
                    : grade
                )
                .join(", ")
            : typeof record.grade === "object" && record.grade !== null
            ? record.grade.grade
            : record.grade || "N/A"}
        </div>
      ),
    },

    {
      key: "age",
      title: "Age",
      minWidth: 94,
      maxWidth: 94,
      sortable: true,
      sortKey: "age",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.age || "16"}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      sortKey: "email",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.email || "N/A"}
        </div>
      ),
    },
    // {
    //   key: "status",
    //   title: "Status",
    //   minWidth: 168,
    //   maxWidth: 168,
    //   align: "center",
    //   sortable: true,
    //   render: (_, record) => (
    //     <Badge
    //       variant={
    //         record.course_completion === "0%"
    //           ? "danger"
    //           : record.course_completion === "100%"
    //           ? "success"
    //           : "warning"
    //       }
    //     >
    //       {record.course_completion === "0%"
    //         ? "Pending"
    //         : record.course_completion === "100%"
    //         ? "Completed"
    //         : "In Progress"}
    //     </Badge>
    //   ),
    // },
    // {
    //   key: "teacher",
    //   title: "Teacher Assigned",
    //   minWidth: 204,
    //   maxWidth: 204,
    //   render: (_, record) => (
    //     <div
    //       className="text-[#1F1F1F] text-[16px] font-medium"
    //       style={{
    //         overflowWrap: "anywhere",
    //         whiteSpace: "pre-wrap",
    //       }}
    //     >
    //       {Array.isArray(record.teachers)
    //         ? record.teachers.map((teacher) => teacher.name).join(", ")
    //         : record.teachers || "N/A"}
    //     </div>
    //   ),
    // },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
      ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
      ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 202,
      maxWidth: 202,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
            userRole?.permissions as string[]
          ) && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="View"
              onClick={() =>
                navigate(`/user-management/student/view-student/${record.id}`)
              }
            >
              <Eye className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}
          {/* {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
            userRole?.permissions as string[]
          ) && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Edit"
              onClick={() =>
                navigate(`/user-management/student/edit-student/${record.id}`)
              }
            >
              <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
            userRole?.permissions as string[]
          ) && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Delete"
              onClick={() => {
                setDeleteModalOpen(true);
                setStudentToDelete(record.id);
              }}
            >
              <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )} */}
        </div>
      ),
    });
  }

  // Handle delete student
  const handleDeleteStudent = () => {
    if (studentToDelete) {
      dispatch(deleteExistingStudent(studentToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setStudentToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete Student:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle export of student data as Excel, PDF or CSV
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: StudentQuery = {
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
        // teacherId: teacherId,
        schoolId: selectedSchool || undefined,
        completion: true,
        type: selectedListType,
      };
      const response = await downloadStudentService(type, queryParams);
      downloadFileFromBlob(response, type, "student_report");
      successToast(
        "Export Successful",
        `Students exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting students as ${type}:`, error);
    }
  };

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  // Handle school selection change
  const handleSchoolChange = (schoolId: string) => {
    setSelectedSchool(schoolId);
    // Reset to first page when school filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleListTypeChange = (type: StudentListType) => {
    setSelectedListType(type);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Fetch school options for the dropdown when component mounts
  useEffect(() => {
    const fetchSchoolOptions = async () => {
      try {
        const response = await getSchoolOptionsService();
        if (response.data.data) {
          setSchoolOptions(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching school options:", error);
      }
    };

    fetchSchoolOptions();
  }, []);

  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Track if component is mounted
  const isMounted = useRef(false);
  // Track if we need to fetch data
  const shouldFetch = useRef(true);

  // Function to fetch students data
  const fetchStudentsData = () => {
    // Prepare sort parameter if sortConfig exists
    let sortBy: string | undefined = undefined;
    if (sortConfig) {
      const apiSortKey = sortKeyMap[sortConfig.key];
      if (apiSortKey) {
        sortBy = `${apiSortKey}`;
      }
    }

    // Only fetch if we should
    if (shouldFetch.current) {
      shouldFetch.current = false; // Prevent additional fetches until explicitly allowed

      dispatch(
        fetchStudents({
          page: currentPage,
          pageSize: ITEMS_PER_PAGE,
          search: searchValue,
          fromDate: fromDate,
          toDate: toDate,
          sortBy: sortBy,
          schoolId: selectedSchool,
          completion: true,
          type: selectedListType,
        })
      );
    }
  };

  // Handle search value changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Reset to first page when search value changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Don't fetch here, let the page change trigger it
    } else {
      // Allow fetching
      shouldFetch.current = true;
      fetchStudentsData();
    }
  }, [searchValue]);

  // Handle school selection changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Reset to first page when school filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Don't fetch here, let the page change trigger it
    } else {
      // Allow fetching
      shouldFetch.current = true;
      fetchStudentsData();
    }
  }, [selectedSchool, selectedListType]);

  // Handle date range changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Don't fetch here, let the page change trigger it
    } else {
      // Allow fetching
      shouldFetch.current = true;
      fetchStudentsData();
    }
  }, [fromDate, toDate]);

  // Handle sort changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Allow fetching
    shouldFetch.current = true;
    fetchStudentsData();
  }, [sortConfig]);

  // Handle page changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Allow fetching
    shouldFetch.current = true;
    fetchStudentsData();
  }, [currentPage]);

  // Initial fetch on component mount
  useEffect(() => {
    isMounted.current = true;
    fetchStudentsData();

    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title="Completed Course Student List"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        leftAction={
          <>
            <StudentTypeSelect
              value={selectedListType}
              onChange={handleListTypeChange}
              className="min-w-[200px] w-full shrink-0 sm:w-[200px]"
            />
            <div className="min-w-[200px] w-full shrink-0 sm:w-[250px]">
              <SchoolSelect
                label=""
                placeholder="Select School"
                options={schoolOptions.map((option) => ({
                  value: option.id,
                  label: option.name,
                }))}
                value={selectedSchool}
                onChange={handleSchoolChange}
                showAbove={false}
                maxHeight="400px"
              />
            </div>
          </>
        }
        rightAction={
          <>
            {/* Export Button */}
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_STUDENT,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={students}
            rowKey="id"
            showHeader={true}
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Students"
      />

      {/* Delete Student Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Student"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete Student ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteStudent()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
